import { useEffect, useRef, useState } from "react";

import { useAppSelector } from "hooks/store";
import { isFetchingDetector, isFetchingPosicheck, requestFailed } from "store/slices/DetectorPage/detectorSlice";
import { openFocusModal } from "store/slices/FocusModal/focusModalSlice";

// This hook is used to count the time for auto refreshing
// It will count from 0 to refreshTime
export const useRefreshCount = (refreshTime: number, isResetModalOpen: boolean = false) => {
  const [count, setCount] = useState<number>(0);
  const detectorDataLoading = useAppSelector(isFetchingDetector);
  const isPosicheckLoading = useAppSelector(isFetchingPosicheck);
  const isRequestFailed = useAppSelector(requestFailed);
  const isFocusModalOpen = useAppSelector(openFocusModal);
  const intervalId = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Helper to determine if counting should be paused
    const shouldPause = () => isFocusModalOpen || isRequestFailed || isPosicheckLoading || isResetModalOpen;

    // Start the interval
    intervalId.current = setInterval(() => {
      setCount((prevCount) => {
        if (shouldPause()) return prevCount;
        const newCount = prevCount + 1;
        return newCount;
      });
    }, 1000);

    // Reset to 0 if threshold is reached
    if (count >= refreshTime && !detectorDataLoading && !isPosicheckLoading) {
      setCount(0);
    }

    // Cleanup on unmount
    return () => {
      if (intervalId.current) clearInterval(intervalId.current);
    };
  }, [
    count,
    refreshTime,
    isResetModalOpen,
    isFocusModalOpen,
    isRequestFailed,
    isPosicheckLoading,
    detectorDataLoading,
  ]);

  // This function is used to reset the count to the same value as refreshTime
  // Helpful when we need to refresh the data without waiting for the full refreshTime
  // Example when we reset a detector, the metrics, examples and other data should be refreshed immediately.
  const reachRefreshTime = () => {
    setCount(refreshTime);
  };

  return { count, reachRefreshTime };
};
